import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { Edit2, Trash2, ChevronDown, ChevronRight, User, Wallet, RefreshCw } from 'lucide-react';

// Types matching backend DTOs
interface HouseholdDTO {
  id: number;
  householdId: string;
  householdName: string;
  primaryContactName: string;
  primaryEmail: string;
  primaryPhone: string;
  householdType: string;
  riskTolerance: string;
  investmentObjective: string;
  serviceTier: string;
  totalAum: number;
  accountCount: number;
  isActive: boolean;
  createdAt: string;
}

interface ClientDTO {
  id: number;
  clientId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  householdId: number;
  householdName: string;
  clientType: string;
  relationshipToPrimary: string;
  riskTolerance: string;
  isActive: boolean;
}

interface PortfolioDTO {
  id: number;
  portfolioName: string;
  portfolioCode: string;
  accountNumber: string;
  accountType: string;
  description: string;
  householdId: number;
  clientId: number;
  currentValue: number;
  status: string;
}

// Mapped frontend types
interface Account {
  id: number;
  accountNumber: string;
  accountName: string;
  accountType: string;
  aum: number;
  status: string;
  modelPortfolio?: string;
  clientId?: number;
}

interface Client {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accounts: Account[];
}

interface Household {
  id: number;
  name: string;
  primaryContact: string;
  email: string;
  phone: string;
  totalAum: number;
  status: string;
  riskProfile: string;
  createdAt: string;
  clients: Client[];
  householdAccounts: Account[];
}

const Container = styled.div<{ embedded?: boolean }>`
  padding: ${props => props.embedded ? '1.5rem' : '2rem'};
  background: ${props => props.embedded ? 'transparent' : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'};
  min-height: ${props => props.embedded ? 'auto' : 'calc(100vh - 64px)'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' | 'ghost' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      transform: translateY(-1px);
    }
  ` : props.variant === 'danger' ? `
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  ` : props.variant === 'ghost' ? `
    background: transparent;
    color: #94a3b8;
    border: none;
    padding: 0.5rem;
    &:hover {
      color: #e2e8f0;
      background: rgba(255, 255, 255, 0.05);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &::placeholder {
    color: #64748b;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 1rem;
  color: #e2e8f0;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const Tr = styled.tr<{ isExpanded?: boolean }>`
  transition: background 0.2s;
  cursor: pointer;
  background: ${props => props.isExpanded ? 'rgba(59, 130, 246, 0.05)' : 'transparent'};

  &:hover {
    background: ${props => props.isExpanded ? 'rgba(59, 130, 246, 0.08)' : 'rgba(255, 255, 255, 0.03)'};
  }
`;

const ExpandedRow = styled.tr`
  background: rgba(15, 23, 42, 0.5);
`;

const ExpandedContent = styled.td`
  padding: 0 !important;
`;

const NestedSection = styled.div`
  padding: 1rem 1rem 1rem 3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #94a3b8;
  gap: 0.5rem;
`;

const ClientCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ClientHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ClientName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #ffffff;
`;

const ClientMeta = styled.div`
  font-size: 0.75rem;
  color: #64748b;
`;

const AccountsList = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AccountIcon = styled.div<{ type: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;

  ${props => {
    const type = props.type?.toUpperCase() || '';
    if (type.includes('IRA') && !type.includes('ROTH')) {
      return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
    } else if (type.includes('ROTH')) {
      return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
    } else if (type.includes('TRUST')) {
      return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
    } else if (type.includes('INDIVIDUAL') || type.includes('TAXABLE')) {
      return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
    } else if (type.includes('JOINT')) {
      return 'background: rgba(236, 72, 153, 0.2); color: #ec4899;';
    } else if (type.includes('401K')) {
      return 'background: rgba(249, 115, 22, 0.2); color: #f97316;';
    } else if (type.includes('529')) {
      return 'background: rgba(14, 165, 233, 0.2); color: #0ea5e9;';
    } else {
      return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const AccountDetails = styled.div``;

const AccountName = styled.div`
  font-weight: 500;
  color: #e2e8f0;
  font-size: 0.875rem;
`;

const AccountMeta = styled.div`
  font-size: 0.7rem;
  color: #64748b;
`;

const AccountAum = styled.div`
  font-weight: 600;
  color: #ffffff;
  font-family: 'SF Mono', monospace;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    const status = props.status?.toUpperCase() || '';
    if (status === 'ACTIVE' || status === 'TRUE') {
      return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
    } else if (status === 'INACTIVE' || status === 'FALSE') {
      return 'background: rgba(239, 68, 68, 0.2); color: #ef4444;';
    } else if (status === 'PROSPECT' || status === 'PENDING') {
      return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
    } else {
      return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const ActionCell = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 0.5rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.variant === 'delete' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  color: ${props => props.variant === 'delete' ? '#ef4444' : '#3b82f6'};

  &:hover {
    background: ${props => props.variant === 'delete' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;

  &:hover {
    color: #ffffff;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const HouseholdAccountsSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(34, 197, 94, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 12px;
`;

const SectionTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: #22c55e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #64748b;
`;

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getAccountTypeLabel = (type: string | null | undefined) => {
  if (!type) return '?';
  const upperType = type.toUpperCase();
  if (upperType.includes('ROTH')) return 'Roth';
  if (upperType.includes('IRA')) return 'IRA';
  if (upperType.includes('TRUST')) return 'Trust';
  if (upperType.includes('INDIVIDUAL') || upperType.includes('TAXABLE')) return 'Tax';
  if (upperType.includes('JOINT')) return 'Joint';
  if (upperType.includes('401K')) return '401k';
  if (upperType.includes('529')) return '529';
  if (upperType.includes('CUSTODIAL') || upperType.includes('UTMA')) return 'UTMA';
  if (upperType.includes('CHARITABLE')) return 'Char';
  return type.substring(0, 4);
};

interface HouseholdsScreenProps {
  embedded?: boolean;
  triggerNew?: number;
}

const HouseholdsScreen: React.FC<HouseholdsScreenProps> = ({ embedded = false, triggerNew = 0 }) => {
  const [households, setHouseholds] = useState<Household[]>([]);
  const [totalClientCount, setTotalClientCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedData, setExpandedData] = useState<Map<number, { clients: Client[]; householdAccounts: Account[]; loading: boolean }>>(new Map());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHousehold, setSelectedHousehold] = useState<Household | null>(null);
  const [formData, setFormData] = useState({
    householdName: '',
    primaryContactName: '',
    primaryEmail: '',
    primaryPhone: '',
    riskTolerance: 'MODERATE',
    householdType: 'FAMILY',
  });

  // Fetch households and client count from API
  const fetchHouseholds = useCallback(async () => {
    setLoading(true);
    try {
      const [householdsRes, clientsRes] = await Promise.all([
        axios.get<HouseholdDTO[]>('/api/advisor/households/all'),
        axios.get<ClientDTO[]>('/api/advisor/clients/all'),
      ]);

      const mapped: Household[] = householdsRes.data.map(h => ({
        id: h.id,
        name: h.householdName,
        primaryContact: h.primaryContactName || '',
        email: h.primaryEmail || '',
        phone: h.primaryPhone || '',
        totalAum: h.totalAum || 0,
        status: h.isActive ? 'ACTIVE' : 'INACTIVE',
        riskProfile: h.riskTolerance || 'MODERATE',
        createdAt: h.createdAt || new Date().toISOString(),
        clients: [],
        householdAccounts: [],
      }));
      setHouseholds(mapped);
      setTotalClientCount(clientsRes.data.length);
    } catch (error) {
      console.error('Failed to fetch households:', error);
      setHouseholds([]);
      setTotalClientCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch clients and accounts for expanded household
  const fetchHouseholdDetails = useCallback(async (householdId: number) => {
    setExpandedData(prev => new Map(prev).set(householdId, { clients: [], householdAccounts: [], loading: true }));

    try {
      const [clientsRes, portfoliosRes] = await Promise.all([
        axios.get<ClientDTO[]>(`/api/advisor/clients/by-household/${householdId}`),
        axios.get<PortfolioDTO[]>(`/api/dimensions/portfolios/by-household/${householdId}`),
      ]);

      const clients = clientsRes.data;
      const portfolios = portfoliosRes.data;

      // Group accounts by client
      const clientMap = new Map<number, Client>();
      clients.forEach(c => {
        clientMap.set(c.id, {
          id: c.id,
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email || '',
          phone: c.phone || '',
          accounts: [],
        });
      });

      const householdAccounts: Account[] = [];

      portfolios.forEach(p => {
        const account: Account = {
          id: p.id,
          accountNumber: p.accountNumber || p.portfolioCode || '',
          accountName: p.portfolioName,
          accountType: p.accountType || 'TAXABLE',
          aum: p.currentValue || 0,
          status: p.status || 'ACTIVE',
          modelPortfolio: p.description,
          clientId: p.clientId,
        };

        if (p.clientId && clientMap.has(p.clientId)) {
          clientMap.get(p.clientId)!.accounts.push(account);
        } else {
          // Household-level account (trust, joint)
          householdAccounts.push(account);
        }
      });

      setExpandedData(prev => new Map(prev).set(householdId, {
        clients: Array.from(clientMap.values()),
        householdAccounts,
        loading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch household details:', error);
      setExpandedData(prev => new Map(prev).set(householdId, { clients: [], householdAccounts: [], loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchHouseholds();
  }, [fetchHouseholds]);

  useEffect(() => {
    if (triggerNew > 0) {
      setSelectedHousehold(null);
      setFormData({
        householdName: '',
        primaryContactName: '',
        primaryEmail: '',
        primaryPhone: '',
        riskTolerance: 'MODERATE',
        householdType: 'FAMILY',
      });
      setIsModalOpen(true);
    }
  }, [triggerNew]);

  const toggleRow = (id: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        // Fetch details when expanding
        if (!expandedData.has(id)) {
          fetchHouseholdDetails(id);
        }
      }
      return newSet;
    });
  };

  const filteredHouseholds = households.filter(h => {
    const matchesSearch = h.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         h.primaryContact.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || h.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalAum = households.reduce((sum, h) => sum + (h.totalAum || 0), 0);
  const totalClients = totalClientCount;
  const totalAccounts = households.reduce((sum, h) => {
    const data = expandedData.get(h.id);
    if (!data) return sum;
    const clientAccounts = data.clients.reduce((s, c) => s + c.accounts.length, 0);
    return sum + clientAccounts + data.householdAccounts.length;
  }, 0);

  const handleEdit = (household: Household, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedHousehold(household);
    setFormData({
      householdName: household.name,
      primaryContactName: household.primaryContact,
      primaryEmail: household.email,
      primaryPhone: household.phone,
      riskTolerance: household.riskProfile,
      householdType: 'FAMILY',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (household: Household, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${household.name}"?`)) return;

    try {
      await axios.delete(`/api/advisor/households/${household.id}`);
      setHouseholds(prev => prev.filter(h => h.id !== household.id));
    } catch (error) {
      console.error('Failed to delete household:', error);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        householdName: formData.householdName,
        primaryContactName: formData.primaryContactName,
        primaryEmail: formData.primaryEmail,
        primaryPhone: formData.primaryPhone,
        riskTolerance: formData.riskTolerance,
        householdType: formData.householdType,
      };

      if (selectedHousehold) {
        await axios.put(`/api/advisor/households/${selectedHousehold.id}`, payload);
      } else {
        await axios.post('/api/advisor/households', payload);
      }
      setIsModalOpen(false);
      fetchHouseholds();
    } catch (error) {
      console.error('Failed to save household:', error);
    }
  };

  if (loading) {
    return (
      <Container embedded={embedded}>
        <LoadingSpinner>
          <RefreshCw className="w-5 h-5 animate-spin" />
          Loading households...
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Households</Title>
          <HeaderActions>
            <Button variant="secondary" onClick={fetchHouseholds}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button variant="primary" onClick={() => {
              setSelectedHousehold(null);
              setFormData({ householdName: '', primaryContactName: '', primaryEmail: '', primaryPhone: '', riskTolerance: 'MODERATE', householdType: 'FAMILY' });
              setIsModalOpen(true);
            }}>
              + New Household
            </Button>
          </HeaderActions>
        </Header>
      )}

      <StatsGrid>
        <StatCard>
          <StatValue>{households.length}</StatValue>
          <StatLabel>Total Households</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(totalAum)}</StatValue>
          <StatLabel>Total AUM</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalClients || '-'}</StatValue>
          <StatLabel>Total Clients</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{totalAccounts || '-'}</StatValue>
          <StatLabel>Total Accounts</StatLabel>
        </StatCard>
      </StatsGrid>

      <SearchBar>
        <SearchInput
          type="text"
          placeholder="Search households..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </Select>
      </SearchBar>

      {filteredHouseholds.length === 0 ? (
        <EmptyState>
          <p>No households found</p>
          <p>Create a new household to get started</p>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th style={{ width: '40px' }}></Th>
              <Th>Household</Th>
              <Th>Primary Contact</Th>
              <Th>Accounts</Th>
              <Th>Total AUM</Th>
              <Th>Risk Profile</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredHouseholds.map((household) => {
              const isExpanded = expandedRows.has(household.id);
              const details = expandedData.get(household.id);
              const accountCount = details
                ? details.clients.reduce((s, c) => s + c.accounts.length, 0) + details.householdAccounts.length
                : '-';

              return (
                <React.Fragment key={household.id}>
                  <Tr isExpanded={isExpanded} onClick={() => toggleRow(household.id)}>
                    <Td>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                    </Td>
                    <Td style={{ fontWeight: 600 }}>{household.name}</Td>
                    <Td>{household.primaryContact}</Td>
                    <Td>{accountCount}</Td>
                    <Td style={{ fontFamily: 'SF Mono, monospace' }}>{formatCurrency(household.totalAum)}</Td>
                    <Td>{household.riskProfile}</Td>
                    <Td>
                      <StatusBadge status={household.status}>{household.status}</StatusBadge>
                    </Td>
                    <Td>
                      <ActionCell>
                        <ActionButton variant="edit" onClick={(e) => handleEdit(household, e)}>
                          <Edit2 className="w-4 h-4" />
                        </ActionButton>
                        <ActionButton variant="delete" onClick={(e) => handleDelete(household, e)}>
                          <Trash2 className="w-4 h-4" />
                        </ActionButton>
                      </ActionCell>
                    </Td>
                  </Tr>

                  {isExpanded && (
                    <ExpandedRow>
                      <ExpandedContent colSpan={8}>
                        <NestedSection>
                          {details?.loading ? (
                            <LoadingSpinner>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Loading details...
                            </LoadingSpinner>
                          ) : (
                            <>
                              {/* Clients with their accounts */}
                              {details?.clients.map(client => (
                                <ClientCard key={client.id}>
                                  <ClientHeader>
                                    <ClientName>
                                      <User className="w-4 h-4 text-blue-400" />
                                      {client.firstName} {client.lastName}
                                    </ClientName>
                                    <ClientMeta>{client.email} {client.phone && `• ${client.phone}`}</ClientMeta>
                                  </ClientHeader>

                                  {client.accounts.length > 0 && (
                                    <AccountsList>
                                      {client.accounts.map(account => (
                                        <AccountRow key={account.id}>
                                          <AccountInfo>
                                            <AccountIcon type={account.accountType}>
                                              {getAccountTypeLabel(account.accountType)}
                                            </AccountIcon>
                                            <AccountDetails>
                                              <AccountName>{account.accountName}</AccountName>
                                              <AccountMeta>
                                                {account.accountNumber}
                                                {account.modelPortfolio && ` • ${account.modelPortfolio}`}
                                              </AccountMeta>
                                            </AccountDetails>
                                          </AccountInfo>
                                          <AccountAum>{formatCurrency(account.aum)}</AccountAum>
                                        </AccountRow>
                                      ))}
                                    </AccountsList>
                                  )}
                                </ClientCard>
                              ))}

                              {/* Household-level accounts (trusts, joint) */}
                              {details?.householdAccounts && details.householdAccounts.length > 0 && (
                                <HouseholdAccountsSection>
                                  <SectionTitle>
                                    <Wallet className="w-3.5 h-3.5" />
                                    Household Accounts
                                  </SectionTitle>
                                  {details.householdAccounts.map(account => (
                                    <AccountRow key={account.id}>
                                      <AccountInfo>
                                        <AccountIcon type={account.accountType}>
                                          {getAccountTypeLabel(account.accountType)}
                                        </AccountIcon>
                                        <AccountDetails>
                                          <AccountName>{account.accountName}</AccountName>
                                          <AccountMeta>
                                            {account.accountNumber}
                                            {account.modelPortfolio && ` • ${account.modelPortfolio}`}
                                          </AccountMeta>
                                        </AccountDetails>
                                      </AccountInfo>
                                      <AccountAum>{formatCurrency(account.aum)}</AccountAum>
                                    </AccountRow>
                                  ))}
                                </HouseholdAccountsSection>
                              )}

                              {(!details?.clients || details.clients.length === 0) && (!details?.householdAccounts || details.householdAccounts.length === 0) && (
                                <EmptyState>No clients or accounts found for this household</EmptyState>
                              )}
                            </>
                          )}
                        </NestedSection>
                      </ExpandedContent>
                    </ExpandedRow>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{selectedHousehold ? 'Edit Household' : 'New Household'}</ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>&times;</CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>Household Name</Label>
            <Input
              type="text"
              value={formData.householdName}
              onChange={(e) => setFormData({ ...formData, householdName: e.target.value })}
              placeholder="Enter household name"
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Primary Contact</Label>
              <Input
                type="text"
                value={formData.primaryContactName}
                onChange={(e) => setFormData({ ...formData, primaryContactName: e.target.value })}
                placeholder="Contact name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.primaryEmail}
                onChange={(e) => setFormData({ ...formData, primaryEmail: e.target.value })}
                placeholder="Email address"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Phone</Label>
              <Input
                type="tel"
                value={formData.primaryPhone}
                onChange={(e) => setFormData({ ...formData, primaryPhone: e.target.value })}
                placeholder="Phone number"
              />
            </FormGroup>
            <FormGroup>
              <Label>Risk Tolerance</Label>
              <Select
                value={formData.riskTolerance}
                onChange={(e) => setFormData({ ...formData, riskTolerance: e.target.value })}
                style={{ width: '100%' }}
              >
                <option value="CONSERVATIVE">Conservative</option>
                <option value="MODERATE">Moderate</option>
                <option value="AGGRESSIVE">Aggressive</option>
                <option value="SPECULATIVE">Speculative</option>
              </Select>
            </FormGroup>
          </FormRow>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} style={{ flex: 1 }}>
              {selectedHousehold ? 'Save Changes' : 'Create Household'}
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default HouseholdsScreen;
